import React, { Suspense, lazy, useCallback, useLayoutEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import P from '../../assets/images/p.png';
import BackgroundWaves from '../BackgroundWaves';
import Terminal from '../Terminal';
import TriangularGridBackground from '../TriangularGridBackground';
import usePrefersReducedMotion from '../../hooks/usePrefersReducedMotion';

import './index.scss';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// three.js is only loaded when the landing page needs it, so the rest of the
// page is not delayed by it.
const AnimatedLogo3D = lazy(() => import('../AnimatedLogo3D'));

// Secondary color of the site, used for the arcs and the circuit lines.
const LOGO_COLOR = { r: 162, g: 222, b: 235 };
const LOGO_DELAY_AFTER_TERMINAL = 500;

/**
 * Landing page with greeting, name, terminal intro, navigation buttons and
 * the animated logo, which assembles once the terminal has finished typing.
 */
const Home = () => {
    const { t } = useTranslation();
    const contentRef = useRef(null);
    const reducedMotion = usePrefersReducedMotion();
    const [terminalDone, setTerminalDone] = useState(false);
    const [glassVisible, setGlassVisible] = useState(false);
    const handleTerminalComplete = useCallback(() => setTerminalDone(true), []);
    const showGlass = useCallback(() => setGlassVisible(true), []);
    const logoFrameRef = useRef(null);
    const logoRef = useRef(null);

    // While the pointer moves the logo faces the viewer; while the glow of
    // the grid drifts on its own, the logo turns to follow it.
    const followGlow = useCallback((position, followsPointer) => {
        logoRef.current?.lookAt?.(followsPointer ? null : position);
    }, []);

    // Shapes the glass behind the logo like the tilted board of the 3D logo.
    const shapeGlass = useCallback((points) => {
        const polygon = points.map(([x, y]) => `${x}px ${y}px`).join(', ');
        logoFrameRef.current?.style.setProperty('--glass-clip', `polygon(${polygon})`);
    }, []);

    // Staggered entrance. Runs before the first paint so the items never
    // flash in their final position, and ends within one second so the name
    // and the buttons are visible right away.
    useLayoutEffect(() => {
        if (reducedMotion) return undefined;

        const context = gsap.context(() => {
            // The hover transitions in the stylesheet would otherwise smooth
            // every animation frame and make the entrance lag.
            gsap.set('.intro-item', { transition: 'none' });
            gsap.from('.intro-item', {
                opacity: 0,
                y: 24,
                duration: 0.4,
                ease: 'power2.out',
                stagger: 0.05,
                clearProps: 'opacity,transform,transition',
            });
        }, contentRef);

        return () => context.revert();
    }, [reducedMotion]);

    return (
        <div className="home" id="home">
            <BackgroundWaves />
            <TriangularGridBackground
                primaryColor={{ r: 255, g: 255, b: 255 }}
                backgroundColor='transparent'
                triangleFill={false}
                opacity={0.2}
                ambient
                onGlowMove={followGlow}
            />
            <div className="home-content" ref={contentRef}>
                <h3 className='intro-item'>{t('greeting')}</h3>
                <h1 className='intro-item'><img className='p' src={P} alt=''></img>ius <span className='surname'>Maushart</span></h1>
                <Terminal className='intro-item' onComplete={handleTerminalComplete} />
                <p className='home-subtitle intro-item'>{t('subtitle')}</p>
                <div className='buttons-container intro-item'>
                    <Link to="/about" className='flat-button'>{t('about_button')}</Link>
                    <Link to="/portfolio" className='flat-button'>{t('portfolio_button')}</Link>
                    <Link to="/contact" className='flat-button'>{t('contact_button')}</Link>
                </div>
                <div className='background' />
                <div className='hoverbackground' />
            </div>
            <div className={`home-img${glassVisible ? ' glass-visible' : ''}`} ref={logoFrameRef}>
                {/* The delay is only set once the terminal is done, so the logo
                    assembles LOGO_DELAY_AFTER_TERMINAL ms after that. */}
                <Suspense fallback={<div className='home-logo' />}>
                    <AnimatedLogo3D
                        ref={logoRef}
                        className='home-logo'
                        logoColor={LOGO_COLOR}
                        circuitColor={LOGO_COLOR}
                        autoAssembleDelay={terminalDone ? LOGO_DELAY_AFTER_TERMINAL : undefined}
                        onCircuitStart={showGlass}
                        onOutline={shapeGlass}
                        aria-hidden="true"
                    />
                </Suspense>
            </div>
        </div>
    );
};

export default Home;
